import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Builder } from '@builder.io/react';
import * as Sentry from '@sentry/nextjs';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, useStore } from 'react-redux';
import styled from 'styled-components';

import { loadRewrites, getRouteInfo } from '@techstyle/next-routes';
import { loadActivatedTests } from '@techstyle/react-abtest';
import {
  getMembership,
  loadMembership,
  loadMembershipPeriod,
  loadProfile,
} from '@techstyle/react-accounts';
import { loadAssets } from '@techstyle/react-assets';
import {
  CategoryLabelProvider,
  desktop,
  FilterFieldLabelProvider,
  GridProvider,
  OptionLabelProvider,
  ProductBrowserCustomFilters as ProductBrowser,
  LoadingStatus,
  useBreakpoint,
  useGridState,
  useProductFiltersCustomFilters,
} from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';
import { loadNav } from '@techstyle/react-navigation';
import {
  loadCategoryBreadcrumbs,
  loadProduct,
  loadProducts,
  parseProductListingQueryParamsCustomFilters,
  ProductListViewedProvider,
} from '@techstyle/react-products';
import {
  getSession,
  loadSession,
  useSession,
  useDomain,
  urlNoQueryString,
} from '@techstyle/redux-core';

import createPage from '../registry';
import { getQuizDetails } from '../src/actions/quiz';
import ActiveCategoryHeading from '../src/components/ActiveCategoryHeading';
import { Component as ByoExitModal } from '../src/components/ByoExitModal';
import { useByoContext } from '../src/components/ByoProvider';
import { Component as ByoStickyProgressBar } from '../src/components/ByoStickyProgressBar';
import CategoryDropdown from '../src/components/CategoryDropdown';
import { Component as EmailNotification } from '../src/components/EmailNotification';
import FilterDrawer from '../src/components/FilterDrawer';
import FilterTags from '../src/components/FilterTags';
import { BuilderGridFilterBar } from '../src/components/GridFilterBar';
import { GridPageNumProvider } from '../src/components/GridPageNumProvider/GridPageNumProvider';
import { Container as MainTemplate } from '../src/components/MainTemplate';
import ProductBottomText from '../src/components/ProductBottomText';
import ProductCategoryTree from '../src/components/ProductCategoryTree';
import {
  ProductGrid,
  ProductGridProductCell,
  ProductGridContent,
  ProductGridBody,
  ProductGridSideBar,
  ProductGridCategoryTreeWrapper,
  ProductGridTopCategoryHeading,
  ProductGridDesktopFilterTags,
  ProductGridProductFilters,
  productGridBandSizeCheckboxProps,
  productGridFilterFieldLabelStyle,
  getProductGridListStyle,
  productGridNestedHeadingStyle,
  ProductGridOverlay,
  SidebarStickyWrapper,
  getProductGridNestedListStyle,
  ProductGridWrapper,
  FilterBottomStickyContainer,
  productGridSizeGridListStyle,
} from '../src/components/ProductGrid';
import { Component as ProductGridHead } from '../src/components/ProductGridHead';
import { Component as ProductHeader } from '../src/components/ProductHeader';
import ProgressLoader from '../src/components/ProgressLoader/ProgressLoader';
import SizeFilterModal from '../src/components/SizeFilterModal';
import { Component as StyleContext } from '../src/components/StyleContext';
import { BREAKPOINTS } from '../src/constants/breakpoints';
import builderPageIfPageContent from '../src/utils/builderPageIfPageContent';
import { applyByoFilters } from '../src/utils/byo';
import {
  getCustomerDmgVariant,
  overrideProductsOriginalPayload,
} from '../src/utils/dmg';
import { getFilterBarContent } from '../src/utils/getFilterBarContent';
import { getNonDupedJointProductArray } from '../src/utils/getNonDupedProducts';
import { getLDFlagWithCtx } from '../src/utils/launchDarkly';
import { useLDFlags } from '../src/utils/LD/useLDFlags';
import { loadNorthStarProducts } from '../src/utils/northStarProductsV2';
import { ProductTag } from '../src/utils/productCategories';
import { getPairItWithProductIds } from '../src/utils/productDetail';
import { colorSwatches } from '../src/utils/productFilters';
import {
  attachRewriteFeatureSortToSortOptions,
  defaultSortOptions,
  getSegmentedSortOptions,
  fplSortOption,
  addNewDefaultSortOption,
} from '../src/utils/products';
import {
  applyProfileSizeFilters,
  getProfileSize,
} from '../src/utils/productSizes';
import { getProductSource } from '../src/utils/productSource';
import {
  registerBuilderComponents,
  filterBarComponents,
} from '../src/utils/registerBuilderComponents';
import {
  getSectionsAndQueryFromPath,
  redirectOldSalePages,
} from '../src/utils/routing';
import salesFpls from '../src/utils/salesFpls';
import { getPotentialSegments } from '../src/utils/segmentation';
import {
  getCategoryNav,
  getDefaultProductFields,
  getDefaultProductFilters,
} from '../src/utils/selectors';
import {
  addAllGenderSizesToFilters,
  addMenSizeUndieToFilters,
} from '../src/utils/sizes';
import { useMeasure } from '../src/utils/use-hooks';
import { useHeaderStatus } from '../src/utils/useHeaderStatus';
import {
  useCategoryLabels,
  useFilterFieldLabels,
  useOptionLabels,
} from '../src/utils/useLabels';
import useScrollRestoration from '../src/utils/useScrollRestoration';
import useScrollToAnchor from '../src/utils/useScrollToAnchor';
import { useTrackPromotionClick } from '../src/utils/useTrackPromotionClick';

const BuilderComponent = dynamic(() =>
  import('../src/components/BuilderComponent').then(module => module.default)
);

const imageAspectRatio = 0.75;

const RecommendedProductsWrapper = styled.div`
  padding: 0;
  display: grid;
  grid-row-gap: ${props => props.gap}px;
  grid-column-gap: ${props => props.gap}px;
  grid-template-columns: repeat(${props => props.columns}, 1fr);

  ${desktop`
    padding: ${({ theme }) =>
      `0 ${theme.spacing.small}px 0px ${theme.spacing.small}px`};
  `}
`;

const GridTitle = styled.div`
  padding: ${({ theme }) =>
    `44px ${theme.spacing.small}px ${theme.spacing.moderate}px ${theme.spacing.small}px`};
  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  text-align: center;

  ${desktop`text-align: left`}
`;

const StyledMainTemplate = styled(MainTemplate)`
  overflow-x: unset;
`;

const GoToCheckoutWrapper = styled.div`
  box-shadow: inset 0px 1px 0px ${({ theme }) => theme.colors.gray250};
  position: fixed;
  bottom: 0;
  left: 0;
  background: ${({ theme }) => theme.colors.white};
  width: 100%;
  height: 75px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GoToCheckoutCta = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90%;
  border: 2px solid ${({ theme }) => theme.colors.default};
  border-radius: 22px;
  font-weight: 600;
  line-height: 17px;
  text-align: center;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  padding: 12px 0;
  cursor: pointer;
`;

function RecommendedProductsGrid({ recommendedProducts, productSource }) {
  const { breakpoint } = useGridState();

  return (
    <RecommendedProductsWrapper
      gap={breakpoint.gap}
      columns={breakpoint.columns}
    >
      {recommendedProducts.map((item, index) => (
        <ProductGridProductCell
          gridIndex={index + 1}
          product={item}
          key={'recommended_' + item.masterProductId}
          imageAspectRatio={imageAspectRatio}
          data-autotag="grid-recommended-product"
          productSource={productSource}
        />
      ))}
    </RecommendedProductsWrapper>
  );
}

RecommendedProductsGrid.propTypes = {
  recommendedProducts: PropTypes.array,
};

const ApplyProfileSizeFilters = () => {
  const { setSelectedFilters, appliedFilters } =
    useProductFiltersCustomFilters();
  const profileSizes = useSelector(getProfileSize);
  const appliedFiltersRef = useRef(appliedFilters);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    if (profileSizes) {
      const filtersWithProfileSize = applyProfileSizeFilters({
        profileSizes,
        initialFilters: appliedFiltersRef.current,
      });

      setSelectedFilters(filtersWithProfileSize);
    }
  }, [profileSizes, setSelectedFilters]);

  return null;
};

function ProductListingFallbackPage({
  baseFilters,
  boostedIds,
  excludeMasterProductIds = [],
  filterKeys,
  filterBarEnabled,
  filterBarContent,
  initialFilters,
  initialProducts,
  initialSortOption,
  isByoPage,
  isPostRegPage,
  mySize = true,
  options,
  primaryCategoryId,
  primaryFplId,
  productBrowserKey,
  productSource,
  recommendedProducts,
  routeInfo,
  routeParams,
  shouldHideFilters,
  sortOptions: sortOptionsFromProps,
  urlSortOption,
}) {
  const isInBuilder = Builder.isEditing || Builder.isPreviewing;
  const [sortOptions, setSortOptions] = useState(sortOptionsFromProps);
  const [shouldUpdateSort, setShouldUpdateSort] = useState(false);
  const [newFilterSizeSelected, setNewFilterSizeSelected] = useState('');
  const [newApplyFilters, setNewApplyFilters] = useState(null);
  const router = useRouter();
  const { asPath } = router;
  const { tld } = useDomain();
  const pathWithoutQueryString = urlNoQueryString(asPath);
  const { exitByoFlow, isFlowInitiated, isInterstitialFlow } = useByoContext();
  const shouldShowByoLayout = isByoPage && isFlowInitiated;
  const { section } = getSectionsAndQueryFromPath(asPath);
  const initialAggregations = initialProducts
    ? initialProducts.aggregations
    : undefined;
  const session = useSession();
  const { isServerOnly, isLoggedIn } = session;
  const { isMobile, isDesktop } = useBreakpoint();
  const dispatch = useDispatch();
  const dispatchTrackPromotionClick = useTrackPromotionClick();
  const { networkStatus } = useSelector(state => state.customer);
  useScrollRestoration(router, { toRoutes: ['/shop/'] });
  const prevIsLoggedIn = useRef(isLoggedIn);
  const isLoggedInChanging = prevIsLoggedIn.current !== isLoggedIn;
  const { getState } = useStore();
  const { headerHeight } = useHeaderStatus();

  const {
    hide_categories_filter_bar: hideCategoriesFilterBarFlag,
    'numbered-pagination': gridPaginationEnabled,
    'filtering-and-sorting-ui': showNewFilteringAndSortingUxInDesktopFlag,
    'show-new-filtering-and-sorting-ux-in-mobile':
      showNewFilteringAndSortingUxInMobileFlag,
    'grid-aa-test': isGridAATestEnabled,
  } = useLDFlags();
  const filterBottomStickyContainerRef = useRef();

  const {
    productGridListStyle,
    productGridNestedListStyle,
    openFilterDrawersOnClearAll,
  } = useMemo(() => {
    const useVariant =
      (showNewFilteringAndSortingUxInDesktopFlag && isDesktop) ||
      (showNewFilteringAndSortingUxInMobileFlag && !isDesktop);
    return {
      productGridListStyle: getProductGridListStyle(useVariant),
      productGridNestedListStyle: getProductGridNestedListStyle(useVariant),
      // if on variant, keep filter drawers open on clear all
      openFilterDrawersOnClearAll: useVariant,
    };
  }, [
    showNewFilteringAndSortingUxInDesktopFlag,
    showNewFilteringAndSortingUxInMobileFlag,
    isDesktop,
  ]);

  const shouldDisplayFilterBar = Boolean(filterBarEnabled && filterBarContent);
  const hideCategoriesFilterBar =
    hideCategoriesFilterBarFlag && shouldDisplayFilterBar;
  // this flag could be omitted if we wanted the same behavior for all themes.
  // for the 'v2' theme, if there's a filter bar (and a title on it), we hide the title in
  // the filter sidebar on desktop
  const hideCategoryHeadingIfFilterBarEnabled =
    shouldDisplayFilterBar && isDesktop;
  const shouldShowCategoryHeading = Boolean(
    primaryCategoryId && section && !hideCategoryHeadingIfFilterBarEnabled
  );
  const {
    gridSlotsAsset,
    productsNavContainer = 'product_sidebar_navigation',
  } = routeParams;
  // Category tree based on Navigation data
  const { activeCategory, breadcrumbs } = useSelector(state =>
    getCategoryNav(state, asPath, productsNavContainer)
  );

  const shouldShowCategoryDropdown =
    (activeCategory.subcategories.length > 0 ||
      activeCategory.siblings.length > 0) &&
    !shouldShowByoLayout &&
    !isPostRegPage &&
    !hideCategoriesFilterBar;
  const context = useMemo(() => {
    if (isMobile) {
      return ['shopping', 'mobileProducts'];
    }
    return ['autoHideNav', 'shopping'];
  }, [isMobile]);

  const {
    canonicalPath,
    description,
    feature: routeFeature,
    keywords,
    title,
  } = routeInfo;

  const onFilterSizeClicked = useCallback(
    ({ newSelectedFilterValue, newAllSelectedFilters }) => {
      setNewFilterSizeSelected(newSelectedFilterValue);
      setNewApplyFilters(newAllSelectedFilters);
    },
    [setNewFilterSizeSelected, setNewApplyFilters]
  );

  const onApplyFiltersMobileClick = useCallback(
    appliedFilters => {
      setNewApplyFilters(appliedFilters.aggregationFilter);
    },
    [setNewApplyFilters]
  );

  const fetchRewrites = useCallback(async () => {
    // Whenever we detect that the route has a feature and it has been loaded from the server,
    // we will refetch the route info so that we will have an accurate data for A/B testing.
    // This is due to server side requests being anonymous.
    if ((isServerOnly || isLoggedInChanging) && routeFeature && canonicalPath) {
      const response = await dispatch(
        loadRewrites([canonicalPath], { force: true })
      );

      const { feature } = response.payload[canonicalPath];

      setSortOptions(prev => {
        const state = getState();
        const { session } = state;
        const membership = getMembership(state);
        const potentialSegments = getPotentialSegments({
          membershipState: membership,
          sessionState: session,
        });

        const { sortOptions: newSortOptions } =
          attachRewriteFeatureSortToSortOptions(
            feature,
            prev,
            potentialSegments
          );

        return newSortOptions;
      });
      if (!urlSortOption) {
        setShouldUpdateSort(true);
      }
    }

    if (isLoggedInChanging) {
      prevIsLoggedIn.current = isLoggedIn;
    }
  }, [
    isServerOnly,
    isLoggedInChanging,
    routeFeature,
    canonicalPath,
    dispatch,
    urlSortOption,
    getState,
    isLoggedIn,
  ]);

  useEffect(() => {
    fetchRewrites();
  }, [fetchRewrites]);

  const [, { height: gridHeaderHeight }] = useMeasure();
  const scrollToTopOffset = headerHeight + gridHeaderHeight;

  const { anchor: Anchor, triggerScrollToAnchor } = useScrollToAnchor({
    offset: isDesktop ? scrollToTopOffset : 0,
  });

  return (
    <StyleContext context={context}>
      <Head>
        <link
          rel="canonical"
          href={`https://www.savagex${tld}${pathWithoutQueryString}`}
        />
      </Head>
      <StyledMainTemplate
        hideFooter={shouldShowByoLayout}
        hideHeader={shouldShowByoLayout}
        allowNewPricingSkinnyBanner
      >
        <ProductGridHead
          title={title}
          description={description}
          keywords={keywords}
        />
        {shouldShowByoLayout ? (
          <>
            <ByoStickyProgressBar isGridPage />
            <ByoExitModal />
          </>
        ) : (
          <ProductHeader hidePromo={!networkStatus.isUpToDate} />
        )}
        {recommendedProducts.length ? (
          <>
            <GridTitle>
              <FormattedMessage
                defaultMessage="Recommended for You"
                id="site_home.recommended_header"
              />
            </GridTitle>
            <GridProvider
              breakpoints={BREAKPOINTS}
              defaultBreakpoint={BREAKPOINTS[0]}
            >
              <RecommendedProductsGrid
                recommendedProducts={recommendedProducts}
                productSource={productSource}
              />
            </GridProvider>
            <GridTitle>
              <FormattedMessage
                defaultMessage="Best Sellers FPO"
                id="site_home.bestsellers_heading"
              />
            </GridTitle>
          </>
        ) : null}
        <LoadingStatus>
          <LoadingStatus.LoadingIndicator>
            <ProgressLoader fixed loading />
          </LoadingStatus.LoadingIndicator>
          {showNewFilteringAndSortingUxInMobileFlag && !isDesktop && <Anchor />}
          <ProductBrowser
            openFilterDrawersOnClearAll={openFilterDrawersOnClearAll}
            activeCategory={activeCategory}
            baseFilters={baseFilters}
            breadcrumbs={breadcrumbs}
            colorSwatches={colorSwatches}
            filterSettings={initialAggregations}
            initialFilters={initialFilters}
            sortOptions={sortOptions}
            initialSortOption={initialSortOption}
            mySize={mySize}
            // In ProductBrowser, we don't currently correctly reset the
            // `productsReducer` state when the component is re-rendered due
            // to a SPA navigation. So even though we'd want its products
            // and `payloadKey` to be re-initialized with the new content
            // from `getInitialProps`, it instead keeps the existing state
            // mounted and thinks the `payloadKey` changed, thus triggering
            // a duplicate request for the first page of products (which
            // already happened in `getInitialProps`). We can fix this, but
            // it will take some work on that component's state lifecycle
            // and maybe adding some refs... for now we instead remount the
            // entire ProductBrowser when the route changes (as determined
            // by `getInitialProps` being called, which will increment the
            // `productBrowserKey`). You might think we can instead use,
            // say, `routeInfo.canonicalPath`, or the serialized
            // `initialFilters` or some prop we already have anyway, but
            // it's possible for the user to re-navigate to the grid page
            // they're already on, so those wouldn't necessarily change.
            key={productBrowserKey}
          >
            <GridPageNumProvider>
              <CategoryLabelProvider useLabels={useCategoryLabels}>
                <FilterFieldLabelProvider
                  useLabels={useFilterFieldLabels.bind(
                    null,
                    showNewFilteringAndSortingUxInDesktopFlag,
                    showNewFilteringAndSortingUxInMobileFlag
                  )}
                >
                  <OptionLabelProvider useLabels={useOptionLabels}>
                    {mySize && <ApplyProfileSizeFilters />}
                    {shouldShowCategoryDropdown && (
                      <ProductBrowser.MobileNavigation>
                        <CategoryDropdown />
                      </ProductBrowser.MobileNavigation>
                    )}
                    <ProductGridBody
                      showGridOnly={isPostRegPage || shouldHideFilters}
                      $enableTopMargin={shouldDisplayFilterBar}
                    >
                      <ProductGridOverlay />
                      {shouldHideFilters || isPostRegPage ? null : (
                        <SidebarStickyWrapper>
                          <ProductGridSideBar>
                            <ProductGridCategoryTreeWrapper
                              hasFilterBar={shouldDisplayFilterBar}
                            >
                              {shouldShowCategoryHeading &&
                              !shouldShowByoLayout ? (
                                <ProductGridTopCategoryHeading />
                              ) : null}
                              {hideCategoriesFilterBar ||
                              shouldShowByoLayout ? null : (
                                <ProductCategoryTree />
                              )}
                              {(showNewFilteringAndSortingUxInDesktopFlag &&
                                isDesktop) ||
                              (showNewFilteringAndSortingUxInMobileFlag &&
                                isMobile) ? (
                                <FilterTags
                                  as={ProductGridDesktopFilterTags}
                                  alwaysShowLabel
                                  headerLabelComponent={
                                    <FormattedMessage
                                      id="site_product_filter.filter_by"
                                      defaultMessage="Filter by"
                                    />
                                  }
                                />
                              ) : (
                                <FilterTags as={ProductGridDesktopFilterTags} />
                              )}
                            </ProductGridCategoryTreeWrapper>

                            <ProductGridProductFilters
                              bandSizeCheckboxProps={
                                productGridBandSizeCheckboxProps
                              }
                              filterFieldLabelStyle={
                                productGridFilterFieldLabelStyle
                              }
                              swatchGridListStyle={productGridListStyle}
                              tagsGridListStyle={productGridListStyle}
                              swatchListStyle={productGridListStyle}
                              listStyle={productGridListStyle}
                              nestedListStyle={productGridNestedListStyle}
                              gridListStyle={productGridSizeGridListStyle}
                              nestedHeadingStyle={productGridNestedHeadingStyle}
                              onFilterSizeClicked={onFilterSizeClicked}
                            />
                          </ProductGridSideBar>
                        </SidebarStickyWrapper>
                      )}

                      {isMobile && !shouldHideFilters ? (
                        <FilterDrawer
                          handleAppliedFilters={onApplyFiltersMobileClick}
                        />
                      ) : null}

                      <ProductListViewedProvider>
                        <ProductGridContent
                          fullWidth={isPostRegPage || shouldHideFilters}
                        >
                          {(shouldDisplayFilterBar || isInBuilder) && (
                            <>
                              {isInBuilder && !filterBarEnabled && (
                                <>
                                  <p>
                                    Note: The{' '}
                                    <strong style={{ fontFamily: 'monospace' }}>
                                      filterBarEnabled
                                    </strong>{' '}
                                    option has not been enabled for this route.
                                  </p>
                                  <p>
                                    This must be set up in the route's CMS
                                    rewrite "Developer-defined JSON" to work
                                    outside of the Builder Editor.
                                  </p>
                                  <pre>
                                    {JSON.stringify(
                                      { filterBarEnabled: true },
                                      null,
                                      2
                                    )}
                                  </pre>
                                </>
                              )}
                              <BuilderComponent
                                model="bmig-grid-filter-bar"
                                content={filterBarContent}
                                context={{
                                  buttonContainerRef:
                                    filterBottomStickyContainerRef,
                                }}
                              />
                            </>
                          )}
                          {!shouldDisplayFilterBar && !isInBuilder && (
                            // We use this instead of GridFilterBar.Root since a lot of the logic is on
                            // BuilderGridFilterBar
                            <BuilderGridFilterBar
                              defaultTitle={<ActiveCategoryHeading />}
                              shortTitle={<ActiveCategoryHeading />}
                              filterItems={null}
                              builderState={{
                                context: {
                                  buttonContainerRef:
                                    filterBottomStickyContainerRef,
                                },
                                state: {
                                  buttonContainerRef:
                                    filterBottomStickyContainerRef,
                                  showMobileFilterButton: true,
                                },
                              }}
                            />
                          )}
                          <GridProvider
                            breakpoints={BREAKPOINTS}
                            defaultBreakpoint={BREAKPOINTS[0]}
                          >
                            {!shouldDisplayFilterBar && isDesktop && <Anchor />}
                            <ProductGridWrapper>
                              <ProductGrid
                                boostedIds={boostedIds}
                                excludeMasterProductIds={
                                  excludeMasterProductIds
                                }
                                filterBarEnabled={shouldDisplayFilterBar}
                                filterKeys={filterKeys}
                                fplId={primaryFplId}
                                gridSlotsAsset={gridSlotsAsset}
                                initialFilters={initialFilters}
                                initialProducts={initialProducts}
                                shouldUpdateSort={shouldUpdateSort}
                                isQuickViewEnabled={isInterstitialFlow}
                                initialSortOption={initialSortOption}
                                options={options}
                                productSource={productSource}
                                routeParams={routeParams}
                                shouldHideFilters={shouldHideFilters}
                                shouldShowByoLayout={shouldShowByoLayout}
                                sortOptions={sortOptions}
                                onProductListUpdate={triggerScrollToAnchor}
                                enablePagination={gridPaginationEnabled}
                              />
                            </ProductGridWrapper>
                          </GridProvider>
                        </ProductGridContent>
                      </ProductListViewedProvider>
                    </ProductGridBody>
                    <EmailNotification
                      onClickEmailBadge={dispatchTrackPromotionClick}
                    />
                    <ProductBottomText />
                  </OptionLabelProvider>
                </FilterFieldLabelProvider>
              </CategoryLabelProvider>
            </GridPageNumProvider>
            <SizeFilterModal
              activeCategory={activeCategory}
              newFilterSizeSelected={newFilterSizeSelected}
              newApplyFilters={newApplyFilters}
              initialFilters={initialFilters}
            />
          </ProductBrowser>
        </LoadingStatus>
        {isMobile && isInterstitialFlow ? (
          <GoToCheckoutWrapper>
            <GoToCheckoutCta
              onClick={() => router.push('/cart').then(() => exitByoFlow())}
              data-autotag="BYONoThanks"
            >
              <FormattedMessage
                id="byo_interstitial_no_thanks_cta"
                defaultMessage="No thanks, Checkout"
              />
            </GoToCheckoutCta>
          </GoToCheckoutWrapper>
        ) : null}
        {isMobile && showNewFilteringAndSortingUxInMobileFlag ? (
          <FilterBottomStickyContainer ref={filterBottomStickyContainerRef} />
        ) : null}
        <div data-tag={`HomePageAATest-${isGridAATestEnabled}`} />
      </StyledMainTemplate>
    </StyleContext>
  );
}

const individualSortOptionPropType = PropTypes.shape({
  value: PropTypes.string.isRequired,
  defaultMessage: PropTypes.string.isRequired,
  isDefault: PropTypes.bool,
  sort: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        field: PropTypes.string,
        direction: PropTypes.oneOf(['asc', 'desc']),
      })
    ),
  ]),
});

ProductListingFallbackPage.propTypes = {
  baseFilters: PropTypes.object,
  boostedIds: PropTypes.array,
  excludeMasterProductIds: PropTypes.array,
  filterBarContent: PropTypes.object,
  filterBarEnabled: PropTypes.bool,
  filterKeys: PropTypes.array,
  initialFilters: PropTypes.object,
  initialProducts: PropTypes.object,
  initialSortOption: individualSortOptionPropType,
  isByoPage: PropTypes.bool,
  isPostRegPage: PropTypes.bool,
  mySize: PropTypes.bool,
  options: PropTypes.object,
  primaryCategoryId: PropTypes.number,
  primaryFplId: PropTypes.number,
  productBrowserKey: PropTypes.number,
  productSource: PropTypes.string,
  recommendedProducts: PropTypes.array,
  routeInfo: PropTypes.object,
  routeParams: PropTypes.object,
  shouldHideFilters: PropTypes.bool,
  sortOptions: PropTypes.arrayOf(individualSortOptionPropType),
  urlSortOption: PropTypes.object,
};

// See the `key` prop of <ProductBrowser/> above for an explanation.
let productBrowserKey = 0;
ProductListingFallbackPage.getInitialProps = async ctx => {
  const { asPath, query, res, store, req, apolloClient } = ctx;
  const anonymousServerSession = req?.context?.anonymousServerSession ?? false;

  const { getState, dispatch } = store;

  const membershipRequests = Promise.all([
    dispatch(loadMembership()),
    dispatch(loadMembershipPeriod()),
  ]);

  const northstarEnabled = await getLDFlagWithCtx({
    flag: 'northstar',
    ctx,
  });

  const gridPaginationEnabled = await getLDFlagWithCtx({
    flag: 'numbered-pagination',
    ctx,
  });

  let initialPageNum = 1;

  if (gridPaginationEnabled && query?.pageNum && !isNaN(query?.pageNum)) {
    const { pageNum } = query;
    initialPageNum = +pageNum;
  }

  let routeInfo = ctx.routeInfo;

  const { canonicalPath } = routeInfo;

  // If ctx.routeInfo doesn't have all the required data, then fetches it.
  // (When the page is reached by clicking a link from the home page,
  // ctx.routeInfo already contains the required data and then there's no
  // need to make this network request.)
  if (!routeInfo.inheritedProductJson) {
    await dispatch(loadRewrites([canonicalPath], { force: true }));
    routeInfo = getRouteInfo(getState(), canonicalPath);
  }
  const {
    inheritedProductJson,
    inheritedRouteParamsJson,
    label,
    productJson,
    routeParamsJson,
    feature = {},
  } = routeInfo;

  const shouldHideFilters = label === 'FPL Auto Filter';

  const { filterBarEnabled: inheritedFilterBar, ...restInheritedRouteParams } =
    inheritedRouteParamsJson || {};
  const routeParams = { ...restInheritedRouteParams, ...routeParamsJson };
  const {
    productsNavContainer = 'product_sidebar_navigation',
    gridSlotsAsset,
    filterBarEnabled,
  } = routeParams;

  const { sort, ...combinedProductJson } = {
    ...inheritedProductJson,
    ...productJson,
  };

  let baseFilters = {
    aggregations: [],
    aggregationFilter: {},
    ...combinedProductJson,
  };

  redirectOldSalePages({ asPath, query, res });

  let { sort: initialSortValue, sortOptions } = routeParams;
  const sortOptionsFromRoute = sortOptions;
  const { productSource: productSourceFromRoute } = routeParams;
  const { root } = getSectionsAndQueryFromPath(asPath);
  const isSalePage = root === 'sale';
  const isByoPage = root === 'byo';
  const isPostRegPage = root === 'quiz-complete';

  if (isPostRegPage && process.browser) {
    await dispatch(loadSession());
    const session = getSession(getState());
    const { dmgVariants } = routeParams;
    const { dmGatewayCode } = session;

    const dmgVariant = getCustomerDmgVariant({
      root,
      dmgVariants,
      dmGatewayCode,
    });

    baseFilters = overrideProductsOriginalPayload({
      originalPayload: baseFilters,
      dmgVariant,
    });
  }

  const primaryFplId =
    (baseFilters.backgroundFplIds && baseFilters.backgroundFplIds[0]) ||
    (baseFilters.fplIds && baseFilters.fplIds[0]);
  const isFplSort = primaryFplId != null;

  // * If sortOptions is explicitly null, we want to hide the dropdown
  let shouldHideSort = sortOptions === null;
  // * Don't allow an object or empty array
  if (!sortOptions || !sortOptions.length) {
    sortOptions = defaultSortOptions;
    if (isFplSort) {
      // * Prepend the FPL sort option as default if it's an FPL page.
      sortOptions = addNewDefaultSortOption(fplSortOption, sortOptions);
    }
  }

  await membershipRequests;

  const membershipState = getMembership(getState());
  const sessionState = getSession(getState());

  const potentialSegments = getPotentialSegments({
    membershipState,
    sessionState,
    anonymousServerSession,
  });

  const { sortOptions: segmentedSortOptions, sortValue: segmentedSortValue } =
    getSegmentedSortOptions({
      sortOptions: sortOptionsFromRoute,
      initialSortOptions: sortOptions,
      potentialSegments,
      initialSortValue,
    });

  sortOptions = segmentedSortOptions;
  initialSortValue = segmentedSortValue;

  if (sortOptions.length === 0) {
    shouldHideSort = true;
  }

  const rewriteFeatureSort = attachRewriteFeatureSortToSortOptions(
    feature,
    sortOptions,
    potentialSegments
  );
  let { initialSortOption } = rewriteFeatureSort;
  sortOptions = rewriteFeatureSort.sortOptions;

  // It's possible for the features used here to add AB tests, so we need to
  // re-fetch activated tests just in case.
  let activatedTestsRequest;
  if (feature?.handle) {
    activatedTestsRequest = dispatch(loadActivatedTests(true));
  }

  const filterBarContent = await getFilterBarContent(
    filterBarEnabled,
    ctx,
    asPath
  );

  const useCategoryIdAggregation = filterBarContent?.builderJson;

  let {
    filters: initialFilters,
    sortOption: urlSortOption,
    mySize = true,
  } = parseProductListingQueryParamsCustomFilters(query, {
    baseFilters,
    filterSettings: baseFilters.aggregations,
    sortOptions,
  });

  const queryIds = query?.product_category_id_list;
  const hasCategoryInQuery = queryIds && queryIds.length > 0;
  const isCategoryNotInFilter =
    !initialFilters?.aggregationFilter?.product_category_id_list;

  if (useCategoryIdAggregation && hasCategoryInQuery && isCategoryNotInFilter) {
    initialFilters.aggregationFilter.product_category_id_list = Array.isArray(
      queryIds
    )
      ? // since queryIds can have be ['123', '234,345', '456']
        // we want to separate '234,345' into 2 separate ids
        queryIds.map(id => id.split(',')).flat()
      : // split by both '-' for each individual slide, and by ',' for slides with multiple ids
        queryIds.split(/[,|-]/);
  }

  if (
    initialFilters.defaultTagId === ProductTag.plusSize ||
    shouldHideFilters
  ) {
    // Disable auto profile sizes on Curvy pages.
    // Disable auto profile sizes when shouldHideFilters is true
    mySize = false;
  }

  if (urlSortOption) {
    initialSortOption = urlSortOption;
  } else if (initialSortValue) {
    initialSortOption = sortOptions.find(
      option => option.value === initialSortValue
    );
  }
  if (!initialSortOption) {
    initialSortOption = isFplSort
      ? fplSortOption
      : sortOptions.find(option => option.isDefault) || sortOptions[0];
  }

  if (shouldHideSort) {
    sortOptions = [];
  }

  const primaryCategoryId =
    (baseFilters.backgroundCategoryIds &&
      baseFilters.backgroundCategoryIds[0]) ||
    (baseFilters.categoryIds && baseFilters.categoryIds[0]) ||
    (baseFilters.defaultCategoryIds && baseFilters.defaultCategoryIds[0]);

  // Start loading as early as possible, but don't await it until the end.
  const breadcrumbsRequest = primaryCategoryId
    ? ctx.store.dispatch(loadCategoryBreadcrumbs(primaryCategoryId))
    : null;

  const navRequest = ctx.store.dispatch(loadNav(productsNavContainer));

  const assetsToLoad = ['products_image_banner'];
  if (gridSlotsAsset) {
    assetsToLoad.push(gridSlotsAsset);
  }

  const assetsRequest = ctx.store.dispatch(loadAssets(assetsToLoad));

  let profileSizes = null;
  const isAnAutoLoginUser = !!(query?.altoken && query?.alkey);
  if (mySize && (process.browser || isAnAutoLoginUser)) {
    // * preload the customer profile to access size preferences
    await ctx.store.dispatch(loadProfile());
    profileSizes = getProfileSize(ctx.store.getState());
  }
  if (profileSizes) {
    initialFilters = applyProfileSizeFilters({
      profileSizes,
      initialFilters,
    });
    // * If the profile size filtering is done on the server, skip it on the client
    mySize = false;
  }

  const options = {
    includeOutOfStock: !isSalePage && !isByoPage && !isPostRegPage,
  };
  // boostedIds will help bring products to the top of the page
  let boostedIds = [];
  if (isByoPage && process.browser) {
    initialFilters = applyByoFilters(initialFilters, !!query.byoInterstitial);
    if (query.lastMasterProductId) {
      const result = await ctx.store.dispatch(
        loadProduct(query.lastMasterProductId)
      );
      boostedIds = getPairItWithProductIds(result.payload);
    }
  }

  if (isPostRegPage && query.mpid) {
    boostedIds = [parseInt(query.mpid)];
  }

  await assetsRequest;
  await navRequest;

  // We shouldn't include the defaults in the `initialFilters` passed to
  // ProductBrowser, otherwise if the defaults change (due to state changing),
  // it wouldn't be possible to *unset* the defaults, since they'd already be
  // included in the `baseFilters` we were passed.
  const defaultFilters = getDefaultProductFilters(
    ctx.store.getState(),
    initialFilters,
    options
  );
  const filters = addMenSizeUndieToFilters(defaultFilters);

  const appendedFilters = addAllGenderSizesToFilters(filters);

  const { breadcrumbs } = getCategoryNav(
    ctx.store.getState(),
    asPath,
    productsNavContainer
  );

  const productSource = getProductSource({
    breadcrumbs,
    urlPath: asPath,
    productSourceFromRoute,
  });

  const experiment36ProductsPerPage = await getLDFlagWithCtx({
    flag: 'experiment-36-products-per-page',
    ctx,
  });

  const pageSize = experiment36ProductsPerPage ? 36 : 24;

  let recommendedProducts = [];
  let excludedProductIds = [];

  if (isPostRegPage && process.browser) {
    const { quizMap } = routeParams;

    if (!getState().membership.customerId) {
      await dispatch(loadProfile());
    }

    const { customerId } = getState().membership;

    if (customerId) {
      const result = await dispatch(getQuizDetails(customerId));

      const categoryIds = result?.payload.reduce((arr, item) => {
        return item.name in quizMap && item.value === 'YES'
          ? [...arr, ...quizMap[item.name].backgroundCategoryIds]
          : arr;
      }, []);

      const colorFilters = [];

      const recommendedProductsResult = await dispatch(
        loadProducts(
          {
            page: initialPageNum,
            size: pageSize,
            sort: initialSortOption.sort,
            boostedIds,
            ...{
              ...filters,
              aggregationFilter: {
                ...filters.aggregationFilter,
                colors: colorFilters,
              },
              ...appendedFilters,
              backgroundCategoryIds: categoryIds,
            },
          },
          { apiVersion: 2, productSource }
        )
      );

      const backupRecommendations = await dispatch(
        loadProducts(
          {
            page: initialPageNum,
            size: pageSize,
            sort: initialSortOption.sort,
            boostedIds,
            ...{
              ...filters,
              aggregationFilter: {},
              backgroundCategoryIds: categoryIds,
            },
          },
          { apiVersion: 2, productSource }
        )
      );

      if (recommendedProductsResult.payload && backupRecommendations.payload) {
        const { products } = recommendedProductsResult.payload;
        const { products: backupProducts } =
          backupRecommendations?.payload || [];
        recommendedProducts = getNonDupedJointProductArray(
          products,
          backupProducts
        ).slice(0, 12);
      }

      if (!recommendedProducts?.length) {
        Sentry.captureException(
          new Error(
            `/products-bmig - root: ${root} - no recommended products are avaialable`
          )
        );
      }

      excludedProductIds = recommendedProducts.map(
        product => product.masterProductId
      );
    } else {
      // This will log because it causes recommendedProducts will return [], which is not what we want. It should always return a recommendation
      Sentry.captureException(
        new Error(
          `/products-bmig - root: ${root} - customerId does not exist. will return [] for recommended products`
        )
      );
    }
  }

  let productsResult = {};

  const isSportGrid = canonicalPath === '/products/sport';
  const shouldUseNorthStar = isSportGrid && northstarEnabled;

  const customFilterForSale = salesFpls({
    excludeFpls: appendedFilters.excludeFpls,
    tld: getState().domain.tld,
    path: asPath,
    isVip: getMembership(getState()),
  });

  const queryParams = {
    page: initialPageNum,
    size: pageSize,
    sort: initialSortOption.sort,
    boostedIds,
    productFields: getDefaultProductFields(),
    ...{
      ...appendedFilters,
      excludeMasterProductIds: excludedProductIds,
    },
    ...customFilterForSale,
  };

  if (shouldUseNorthStar) {
    productsResult.payload = await loadNorthStarProducts(
      apolloClient,
      queryParams,
      {
        includeProductDimensions: true,
        includeProductListing: true,
        includePricing: true,
      }
    );
  } else {
    productsResult = await dispatch(
      loadProducts(queryParams, { apiVersion: 2, productSource })
    );
  }

  const braSizes = (productsResult?.payload?.aggregations || []).find(
    aggr => aggr.field === 'size_bra'
  );

  if (braSizes?.items?.length > 0) {
    // filter all bra size values without a cup size
    braSizes.items = braSizes.items.filter(item => /\d/.test(item.value));
  }

  await breadcrumbsRequest;
  await activatedTestsRequest;

  // See the `key` prop of <ProductBrowser/> above for an explanation.
  if (process.browser) {
    productBrowserKey += 1;
  }

  return {
    baseFilters,
    boostedIds,
    excludeMasterProductIds: excludedProductIds,
    filterKeys: Object.keys(appendedFilters.aggregationFilter || {}),
    initialFilters,
    initialProducts: productsResult.payload,
    initialSortOption,
    isByoPage,
    isPostRegPage,
    mySize,
    options,
    primaryCategoryId,
    primaryFplId,
    productBrowserKey,
    productSource,
    recommendedProducts,
    routeInfo,
    routeParams,
    shouldHideFilters,
    sortOptions,
    urlSortOption,
    filterBarEnabled,
    filterBarContent: filterBarContent?.builderJson,
  };
};

registerBuilderComponents(filterBarComponents);

const ProductListingPage = builderPageIfPageContent(ProductListingFallbackPage);

export default createPage(ProductListingPage, {
  routeInfoRequired: true,
  resourceBundles: [
    'inline_size_grid',
    'site_product_category',
    'site_product_filter',
    'site_product_grid',
    'site_product_option',
    'site_pdp',
    'site_home',
  ],
  prefetchAssets: [
    'site_product_banner',
    'products_image_banner',
    'products_html_skinnybanner',
    'pdp_html_fpl',
  ],
  prefetchFeatures: ['match_user_size_with_model_size', 'northstar'],
  marketingInfo: {
    segmentCategory: 'grid',
  },
});
